// Dashboard — full parity. Read-only aggregation: stat cards with income
// progress, debt summary tiles + alerts, and budget tracking in both
// by-category and grouped-by-type views (filter / sort / fully-spent collapse).
// All derivations reuse the shared lib unchanged; only the view toggles are
// reactive.
import { h, mount, type Child } from '../core/dom';
import { signal, effect } from '../core/signal';
import { text } from '../core/bind';
import { getCurrentMonth, changeMonth, isValidMonthKey } from '@lib/month';
import { navigate } from '../core/router';
import {
	computeAggregates,
	buildCategoryTypeMap,
	computeIncomeProgress,
	formatPctOfIncome,
	groupBudgetByType,
} from '@lib/dashboardData';
import { computeMonthlyBudget, buildIncomeBreakdown, matchBreakdownActuals, debtAccountIdsFromDebts, type CategoryBudgetItem } from '@lib/budget';
import { scheduledIncomeForCycle } from '@lib/schedule';
import { summariseDebts, paymentSourceChip, type DebtSummary } from '@lib/debt';
import { displayCategoryName } from '@lib/category';
import { calculateCategorySummary, formatError, type Budget, type Category, type DebtView, type Transaction } from '@lib/db';
import { formatCurrency } from '@lib/format';
import { loadBudgetData } from '../data';
import { monthNav } from '../components/monthNav';
import { setHeaderControl } from '../components/headerSlot';
import { modal } from '../ui/modal';
import { breakdownBody } from '../components/incomeBreakdown';
import { loading } from '../ui/loading';

export function dashboardRoute(query?: URLSearchParams): Node {
	const container = h('div', {});
	const qMonth = query?.get('month');
	const month = isValidMonthKey(qMonth) ? qMonth : getCurrentMonth();
	// Month changes navigate (the URL is the source of truth for the month), so
	// the view is deep-linkable; the router re-renders the route on hashchange.
	const goto = (off: number) => navigate(`/?month=${changeMonth(month, off)}`);
	render();
	return container;

	function render(): void {
		setHeaderControl(monthNav(month, goto));
		container.replaceChildren(loading());
		loadBudgetData(month)
			.then((d) => build(d.transactions, d.budget, d.categories, d.debts))
			.catch((e) => container.replaceChildren(h('div', { class: 'state-error' }, formatError(e as Error))));
	}

	function build(transactions: Transaction[], budget: Budget | null, categories: Category[], debts: DebtView[]): void {
		const agg = computeAggregates(transactions);
		const typeMap = buildCategoryTypeMap(categories);
		const expected = scheduledIncomeForCycle(budget?.incomeSchedules ?? [], month);
		const incomeProgress = computeIncomeProgress(expected, budget?.plannedIncome ?? 0, agg.totalIncome);
		const totalBudgeted = Object.values(budget?.categoryBudgets ?? {}).reduce((s, v) => s + v, 0);
		const items = computeMonthlyBudget({
			transactions,
			debts,
			categoryBudgets: budget?.categoryBudgets ?? {},
			isNewBudget: false,
			mode: 'spent-or-budgeted',
		}).filter((b) => b.budgeted > 0 || b.spent > 0);
		const catSummary = calculateCategorySummary(transactions, true, true, debtAccountIdsFromDebts(debts));
		const debtSummary = summariseDebts(debts, catSummary);

		const groupByType = signal(false);
		const filterByType = signal<string | null>(null);
		const sortBy = signal<'amount' | 'name' | 'type'>('amount');
		const showFullySpent = signal(false);
		const pct = (amount: number) => formatPctOfIncome(amount, agg.totalIncome);

		function openIncomeModal(): void {
			const rows = matchBreakdownActuals(buildIncomeBreakdown(budget?.incomeSchedules ?? [], month), transactions);
			modal({ title: 'Income Schedule', body: breakdownBody(rows) });
		}

		container.replaceChildren(
			h(
				'div',
				{},
				statCards(),
				debts.length > 0 ? debtBand(debtSummary) : null,
				budgetTracking()
			)
		);

		function statCards(): Child {
			const incomeSub = incomeProgress
				? h(
						'div',
						{ class: 'stat-sub' },
						h('span', { class: `status-pill${incomeProgress.isBehind ? ' behind' : ''}` }, incomeProgress.isBehind ? 'Behind' : 'On track'),
						h('span', { class: 'muted' }, ` ${incomeProgress.label === 'planned' ? 'Planned' : 'Expected by now'}: ${formatCurrency(incomeProgress.value)}`),
						incomeProgress.remaining > 0 && (budget?.incomeSchedules?.length ?? 0) > 0
							? h('button', { class: 'btn-link', onclick: openIncomeModal }, ` ${formatCurrency(incomeProgress.remaining)} still to come`)
							: incomeProgress.remaining > 0
								? h('span', { class: 'muted' }, ` ${formatCurrency(incomeProgress.remaining)} still to come`)
								: null
					)
				: null;
			return h(
				'div',
				{ class: 'stats-grid' },
				h('div', { class: 'stat-card' }, h('div', { class: 'stat-label' }, 'Income'), h('div', { class: 'stat-value income' }, formatCurrency(agg.totalIncome)), incomeSub),
				h(
					'div',
					{ class: 'stat-card' },
					h('div', { class: 'stat-label' }, 'Expenses'),
					h('div', { class: 'stat-value expense' }, formatCurrency(Math.abs(agg.totalExpenses))),
					totalBudgeted > 0 ? h('div', { class: 'stat-sub muted' }, `of ${formatCurrency(totalBudgeted)} budgeted`) : null
				),
				h('div', { class: 'stat-card' }, h('div', { class: 'stat-label' }, 'Net'), h('div', { class: `stat-value ${agg.netBalance >= 0 ? 'income' : 'expense'}` }, formatCurrency(agg.netBalance))),
				h(
					'div',
					{ class: 'stat-card' },
					h('div', { class: 'stat-label' }, 'Transactions'),
					h('div', { class: 'stat-value' }, String(transactions.length)),
					agg.uncategorisedCount > 0 ? h('a', { class: 'stat-sub uncategorised-link', href: `#/transactions?month=${month}` }, `${agg.uncategorisedCount} uncategorised`) : null
				)
			);
		}

		function debtBand(s: DebtSummary): Child {
			// Debt names with a PAYE chip on payroll-deducted debts (Svelte parity).
			const namesLine = (list: DebtView[]): Child =>
				h(
					'div',
					{ class: 'legacy-sub debt-names' },
					...list.flatMap((d, i) => {
						const parts: Child[] = [i > 0 ? ', ' : null, d.name];
						const chip = paymentSourceChip(d);
						if (chip) parts.push(h('span', { class: 'source-chip', title: "Deducted at source via PAYE — not visible in Akahu's transaction feed" }, chip));
						return parts;
					})
				);
			// Tiles deep-link to their class section on the debts page (?class=...).
			const tile = (label: string, debtClass: string, total: number, extra: Child[]): Child =>
				h('a', { class: 'debt-tile debt-link', href: `#/debts?class=${debtClass}` }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-total' }, formatCurrency(total)), ...extra);
			const tiles: Child[] = [];
			if (s.consumer.debts.length) tiles.push(tile('Consumer', 'consumer', s.consumer.total, [namesLine(s.consumer.debts), h('div', { class: 'legacy-sub' }, `${formatCurrency(s.consumer.paidThisMonth)} paid · ${pct(s.consumer.paidThisMonth)}`)]));
			if (s.assetBacked.debts.length) tiles.push(tile('Asset-backed', 'asset_backed', s.assetBacked.total, [namesLine(s.assetBacked.debts), h('div', { class: 'legacy-sub' }, `${s.assetBacked.equityPct}% equity · LVR ${s.assetBacked.lvrPct}%`)]));
			if (s.monitored.debts.length) tiles.push(tile('Monitored', 'monitored', s.monitored.total, [namesLine(s.monitored.debts), h('div', { class: 'legacy-sub' }, 'tracked only')]));

			const alertRows: Child[] = [
				...s.alerts.overdueFacilities.map((d) => h('a', { class: 'alert overdue', href: `#/debts/${encodeURIComponent(d.id)}` }, `⚠ ${d.name} payment overdue${d.guardDueDate ? ` (due ${d.guardDueDate})` : ''}`)),
				...s.alerts.dueFacilities.map((d) => h('a', { class: 'alert due', href: `#/debts/${encodeURIComponent(d.id)}` }, `⚠ ${d.name} payment due${d.guardDueDate ? ` by ${d.guardDueDate}` : ''}`)),
				...s.alerts.coherenceDrifts.map((c) => h('a', { class: 'alert drift', href: `#/debts/${encodeURIComponent(c.debt.id)}` }, `⚠ ${c.debt.name}: ledger drift ${formatCurrency(Math.abs(c.gap))}`)),
			];
			return h(
				'div',
				{ class: 'section' },
				h('div', { class: 'section-head' }, h('h2', {}, 'Debts'), s.alerts.total > 0 ? h('span', { class: 'badge excluded' }, `${s.alerts.total} alerts`) : null),
				h('div', { class: 'debt-grid' }, ...tiles),
				alertRows.length ? h('div', { class: 'alerts' }, ...alertRows) : null
			);
		}

		function budgetTracking(): Child {
			const head = h(
				'div',
				{ class: 'section-head' },
				h('h2', {}, 'Budget tracking'),
				toggleBtn(() => `View: ${groupByType() ? 'type' : 'category'}`, () => { groupByType.update((v) => !v); filterByType.set(null); }),
				toggleBtn(() => `Sort: ${sortBy()}`, () => sortBy.set(sortBy() === 'amount' ? 'name' : 'amount')),
				h('a', { class: 'btn btn-sm btn-secondary', href: `#/budgets?month=${month}` }, 'Edit budget')
			);
			const body = h('div', {});
			mount(body, () => (groupByType() ? typeView() : categoryView()));
			return h('div', { class: 'section' }, head, body);
		}

		function typeView(): Child {
			const rows = groupBudgetByType(items, typeMap, sortBy() === 'amount' ? 'amount' : 'type');
			if (rows.length === 0) return h('p', { class: 'muted' }, 'No spending data to group by type');
			return h('div', { class: 'budget-list' }, ...rows.map((r) =>
				h(
					'div',
					{ class: `budget-row${r.isOverBudget ? ' over-budget' : ''}` },
					h('div', { class: 'budget-row-head' }, h('span', {}, r.type), h('span', {}, `${formatCurrency(r.spent)} / ${formatCurrency(r.budgeted)}`)),
					r.budgeted > 0 ? barFor(r.percentUsed, r.isOverBudget) : null,
					budgetFooter(
						h('button', { class: 'count-link', onclick: () => { filterByType.set(r.type); groupByType.set(false); } }, pluralize(r.categories.length, 'category', 'categories')),
						r.budgeted,
						r.spent,
						r.isOverBudget,
						r.type !== 'Uncategorised'
					)
				)
			));
		}

		function categoryView(): Child {
			const ft = filterByType();
			const base = ft ? items.filter((i) => (typeMap.get(i.name) || 'Uncategorised') === ft) : items;
			const visible = [...base].sort((a, b) =>
				sortBy() === 'name' ? a.name.localeCompare(b.name) : b.spent - a.spent
			);
			if (visible.length === 0) {
				return budget
					? h('p', { class: 'muted' }, 'No expenses this month')
					: h('p', { class: 'muted' }, 'No budget set for this month. ', h('a', { href: `#/budgets?month=${month}` }, 'Create one'));
			}
			const over = visible.filter((b) => (b.isOverBudget || b.isUnbudgeted) && !b.isUncategorised);
			const uncat = visible.filter((b) => b.isUncategorised);
			const under = visible.filter((b) => b.statusGroup === 2 && !b.isUncategorised);
			const fully = visible.filter((b) => b.statusGroup === 1);
			return h(
				'div',
				{ class: 'row-stack' },
				ft ? h('div', { class: 'row' }, h('span', { class: 'badge' }, `type: ${ft}`), h('button', { class: 'btn-link', onclick: () => filterByType.set(null) }, 'clear')) : null,
				...group(over),
				...group(uncat),
				...group(under),
				fully.length
					? h(
							'div',
							{ class: 'row-stack' },
							toggleBtn(() => `${showFullySpent() ? 'Hide' : 'Show'} fully spent (${fully.length})`, () => showFullySpent.update((v) => !v), showFullySpent),
							(() => { const b = h('div', { class: 'row-stack' }); mount(b, () => (showFullySpent() ? group(fully) : null)); return b; })()
						)
					: null
			);
		}

		function group(list: CategoryBudgetItem[]): Child[] {
			return list.map((b) =>
				h(
					'div',
					{ class: `budget-row${b.isOverBudget ? ' over-budget' : ''}${b.isUnbudgeted ? ' unbudgeted' : ''}` },
					h(
						'div',
						{ class: 'budget-row-head' },
						h(
							'span',
							{},
							h('a', { href: `#/transactions?month=${month}&category=${encodeURIComponent(b.name)}` }, displayCategoryName(b.name)),
							debtSummary.servicingCategories.has(b.name) ? h('span', { class: 'debt-chip', title: 'Debt servicing' }, 'debt') : null
						),
						h('span', {}, `${formatCurrency(b.spent)} / ${formatCurrency(b.budgeted)}`)
					),
					b.budgeted > 0 ? barFor(b.percentUsed, b.isOverBudget) : null,
					budgetFooter(
						h('a', { class: 'count-link', href: `#/transactions?month=${month}&category=${encodeURIComponent(b.name)}` }, pluralize(b.count, 'transaction', 'transactions')),
						b.budgeted,
						b.spent,
						b.isOverBudget,
						!b.isUncategorised
					)
				)
			);
		}
	}
}

function pluralize(n: number, one: string, many: string): string {
	return `${n} ${n === 1 ? one : many}`;
}

// Footer under each budget row: a count drill-link on the left, the
// remaining/over amount (or "No budget set") on the right — Svelte parity.
function budgetFooter(countEl: Child, budgeted: number, spent: number, over: boolean, showNoBudget: boolean): Child {
	const status =
		budgeted > 0
			? h('span', { class: `remaining${over ? ' over' : ''}` }, over ? `${formatCurrency(spent - budgeted)} over` : `${formatCurrency(budgeted - spent)} left`)
			: showNoBudget
				? h('span', { class: 'no-budget' }, 'No budget set')
				: null;
	return h('div', { class: 'budget-footer' }, countEl, status);
}

function barFor(percentUsed: number, over: boolean): Child {
	const cls = over ? ' over' : percentUsed > 80 ? ' warning' : '';
	return h(
		'div',
		{
			class: 'progress-bar',
			role: 'progressbar',
			'aria-label': 'Budget used',
			'aria-valuemin': '0',
			'aria-valuemax': '100',
			'aria-valuenow': String(Math.round(Math.min(percentUsed, 100))),
		},
		h('div', { class: `progress-fill${cls}`, style: { width: `${Math.min(percentUsed, 100)}%` } })
	);
}
function toggleBtn(label: () => string, onClick: () => void, expanded?: () => boolean): Child {
	const btn = h('button', { class: 'btn btn-sm btn-secondary', onclick: onClick }, text(label));
	if (expanded) effect(() => btn.setAttribute('aria-expanded', String(expanded())));
	return btn;
}
